/**
 * useApiMutation — l'ÉCRITURE à un coup des ports, en un seul endroit.
 *
 * L'idiome est celui de `ports/CLAUDE.md` § « The write-side variant » : trois gardes — « est-ce
 * NOUS qui avons lancé l'appel » (`awaiting`), « une seule réaction par issue » (`handledError`),
 * et « pour quelle LIGNE » (`pendingId`, comparé aux `routeParams` que la tranche renvoie, sans
 * quoi l'échec d'une ligne s'affiche sur une autre) — la tranche rendue au repos après le succès,
 * après une erreur traitée et au démontage, et un message d'erreur normalisé en chaîne, pour que
 * le design system reçoive de la copie et jamais une enveloppe.
 *
 * `scopeParam` n'est utile que si la tranche peut répondre pour une AUTRE ligne que la nôtre : une
 * surface qui n'en porte qu'une (la fiche d'une activité, une carte de génération) s'en passe.
 * Une écriture qui a besoin d'une garde de plus l'écrit à la main et dit laquelle.
 */
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest, apiRequestWithPrerequest } from "../../../../data/redux/actions/api"
import { apiErrorMessage } from "./apiErrorMessage"
import { type GestureName, useGesture } from "./gestureTracking"
import { studentRefusal } from "./studentRefusal"
import { type ApiService } from "./useApiFetch"

export type ApiMutationSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: unknown
    routeParams?: Record<string, unknown>
}

export type UseApiMutationOptions = {
    service: ApiService
    /** Repli du message normalisé — inutile quand l'appelant lit l'erreur brute. */
    fallback?: string
    /** Le paramètre de route qui dit POUR QUI l'appel est parti (`id`, `student`…). */
    scopeParam?: string
    /** L'appel ne relance pas une écriture déjà en vol — `start()` rend alors `false`. */
    skipWhilePending?: boolean
    /**
     * Demander d'abord le cookie CSRF (`apiRequestWithPrerequest`), pour une route web que Sanctum
     * protège sans jeton : les deux formulaires de mot de passe, et la connexion par mot de passe sur
     * le web. Sa tranche a la même forme.
     */
    prerequest?: boolean
    /** Services à rendre au repos au démontage. Défaut : celui qu'on appelle. */
    idleOnUnmount?: ApiService[]
    /**
     * `"student"` : l'écriture part de l'espace élève — le message d'échec est dit au tutoiement
     * (`studentRefusal`), `fallback` écrit au tutoiement lui aussi. Défaut : la phrase du serveur.
     */
    voice?: "student"
    /**
     * Le geste mesuré (`gestureTracking`) : par défaut le nom du service. Toute écriture émet
     * `gesture.submitted` puis `gesture.succeeded` ou `gesture.failed` ; `false` : un appel qui
     * n'est pas un geste de la personne (à justifier sur la ligne).
     */
    gesture?: GestureName | false
    /** L'écran d'où part le geste, en propriété `surface`. */
    surface?: string
    onSuccess?: (data: unknown) => void
    onError?: (message: string, error: unknown) => void
}

export function useApiMutation(options: UseApiMutationOptions) {
    const { service } = options
    const dispatch = useDispatch()
    // Le nom du service n'est pas un `GestureName` : c'est le seul nom hors de la liste, celui
    // d'une écriture que l'écran n'a pas nommée.
    const gesture = useGesture(
        options.gesture === false ? null : ((options.gesture ?? service.name) as GestureName),
        options.surface
    )

    /** Les options au dernier rendu : les effets les lisent sans se redéclarer à chaque rappel. */
    const optionsRef = useRef(options)
    optionsRef.current = options

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.[service.name] as ApiMutationSlice | undefined
    )

    const awaitingRef = useRef(false)
    const handledErrorRef = useRef(false)
    const pendingIdRef = useRef<unknown>(null)

    /** Rend `false` quand rien n'a été envoyé — le geste qui accompagne l'appel sait se retenir. */
    const start = useCallback(
        (
            routeParams: Record<string, unknown> = {},
            body: Record<string, unknown> = {},
            urlParams: Record<string, unknown> = {}
        ): boolean => {
            const current = optionsRef.current
            if (current.skipWhilePending && awaitingRef.current) return false
            awaitingRef.current = true
            handledErrorRef.current = false
            pendingIdRef.current = current.scopeParam ? (routeParams[current.scopeParam] ?? null) : null
            const request = current.prerequest ? apiRequestWithPrerequest : apiRequest
            dispatch(request(current.service, routeParams, body, urlParams) as never)
            gesture.submitted()
            return true
        },
        // Le service est lu dans le ref, mais l'identité de `start` doit le suivre : un appelant qui
        // mémoïse sur `start` doit repartir quand la porte change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, service, gesture]
    )

    /** La tranche renvoie ses `routeParams` : on ne réagit qu'à l'appel qu'on a lancé. */
    const isOurCall = () => {
        const scope = optionsRef.current.scopeParam
        if (!scope) return true
        const calledFor = req?.routeParams?.[scope]
        return pendingIdRef.current == null || calledFor == null || String(calledFor) === String(pendingIdRef.current)
    }

    useEffect(() => {
        if (!awaitingRef.current || !req?.success || !isOurCall()) return
        awaitingRef.current = false
        pendingIdRef.current = null
        gesture.succeeded()
        optionsRef.current.onSuccess?.(req.data)
        dispatch(apiIdle(optionsRef.current.service) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, req?.success, req?.data, req?.routeParams])

    useEffect(() => {
        if (!awaitingRef.current || !req?.error || handledErrorRef.current || !isOurCall()) return
        handledErrorRef.current = true
        awaitingRef.current = false
        pendingIdRef.current = null
        const current = optionsRef.current
        const fallback = current.fallback ?? ""
        const message =
            current.voice === "student" ? studentRefusal(req.error, fallback) : apiErrorMessage(req.error, fallback)
        gesture.failed(req.error)
        current.onError?.(message, req.error)
        dispatch(apiIdle(current.service) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, req?.error, req?.routeParams])

    // Un reste sur la tranche se rejouerait au prochain montage de l'écran.
    useEffect(
        () => () => {
            const current = optionsRef.current
            for (const idled of current.idleOnUnmount ?? [current.service]) {
                dispatch(apiIdle(idled) as never)
            }
        },
        [dispatch, service]
    )

    // `gesture` : le même traceur, pour l'`opened` de la feuille qui mène à l'écriture.
    return { start, requesting: !!req?.request, gesture }
}
